import { Injectable, signal, computed } from '@angular/core';
import { AcademicYear } from '../models/program-settings.model';
import { EntraUserGroup } from 'projects/libs/models/src/enums/entra-user-group';
import { BannerConfig, BannerIcons, BannerTypeEnum } from '@k12/app-components';

type AdminState = {
    academicYearsList: AcademicYear[],
    selectedAcademicYear: AcademicYear| null,
    userGroups: string[],
}

const initialState: AdminState = {
    academicYearsList: [],
    selectedAcademicYear: null,
    userGroups: [],
}

@Injectable({ providedIn: 'root' })
export class AdminStore {
    // State signals
    private readonly state = signal<AdminState>(initialState);

    // Public read-only signals
    academicYearsList = computed(() => this.state().academicYearsList);
    selectedAcademicYear = computed(() => this.state().selectedAcademicYear);
    userGroups = computed(() => this.state().userGroups);

    // Computed signals
    private readonly hasGlobalAdminRole = computed(() => 
        this.userGroups().includes(EntraUserGroup.GlobalAdmin)
    );

    private readonly hasAdminRole = computed(() => 
        this.userGroups().includes(EntraUserGroup.Admin)
    );

    private readonly isSelectedAcademicYearCurrent = computed(() => {
        const academicYear = this.selectedAcademicYear();
        if (!academicYear) return false;
        const currentDate = new Date();
        return currentDate >= new Date(academicYear.startDate) && currentDate <= new Date(academicYear.endDate);
    });

    private readonly hasEditAccess = computed(() => {
        const academicYear = this.selectedAcademicYear();
        if (!academicYear) return false;
        return this.hasGlobalAdminRole() || (this.hasAdminRole() && this.isSelectedAcademicYearCurrent());
    });

    canEdit = computed(() => this.hasEditAccess());

    /**
     * Role-only admin check. Unlike canEdit this is deliberately not gated on the selected academic
     * year, because it guards read-only navigation (opening a student record) rather than editing —
     * a historical year is still viewable.
     */
    isAdminUser = computed(() => this.hasGlobalAdminRole() || this.hasAdminRole());

    HistoricalYearBannerConfigs = computed(() => {
        const academicYear = this.selectedAcademicYear();
        if (academicYear) {
            const currentDate = new Date();
            if (this.hasGlobalAdminRole() && currentDate > new Date(academicYear.endDate)) {
                return [
                    {
                        id: '2',
                        type: { type: BannerTypeEnum.WARNING },
                        message: `You are viewing information from a previous academic year. Any changes you make will update records for that year.`,
                        title: '',
                        icon: BannerIcons[BannerTypeEnum.WARNING]
                    }
                ] as BannerConfig[];
            } else if (this.hasAdminRole() && !this.isSelectedAcademicYearCurrent()) {
                return [
                    {
                        id: '2',
                        type: { type: BannerTypeEnum.INFO },
                        message: `You are now viewing previous or a future academic year information - read only.`,
                        title: '',
                        icon: BannerIcons[BannerTypeEnum.INFO]
                    }
                ] as BannerConfig[];
            }
        }
        return [] as BannerConfig[];
    });

    // Methods
    init(academicYearsList: AcademicYear[], userGroups: string[]): void {
        const currentDate = new Date();
        const currentAcademicYear = academicYearsList.find(year => 
            currentDate >= new Date(year.startDate) && currentDate <= new Date(year.endDate)
        );
        
        this.state.set({
            academicYearsList,
            selectedAcademicYear: currentAcademicYear || null,
            userGroups,
        });
    }

    setSelectedAcademicYear(selectedAcademicYear: AcademicYear): void {
        this.state.update(state => ({
            ...state,
            selectedAcademicYear,
        }));
    }
}

// Export for backwards compatibility
export const adminStore = AdminStore;